"use client";

import React, { useMemo } from "react";
import { ThemeContext } from "./ThemeContext";
import { darkTheme, lightTheme } from "../tokens/colors";
import { fontFamily, fontSize, fontWeight } from "../tokens/typography";
import type { Density, ThemeMode, ThemeTokens } from "./types";

const RAMP_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
const SEMANTIC_KEYS = ["success", "warning", "error", "info"] as const;
const FONT_SIZE_KEYS = Object.keys(fontSize) as Array<keyof typeof fontSize>;

// M3 role tokens are fixed swatches (same in both themes, like accent/neutral
// above) — camelCase key on M3RoleTokens paired with its kebab-case
// `--ds-color-*` suffix. `error`/`on-error`/`error-container`/
// `on-error-container` are new, distinct var names from the existing
// `error-bg`/`error-border`/`error-fg`/`error-solid`/`error-on-solid`
// semantic-status vars below — both coexist on purpose, see TOKENS.md.
const M3_ROLE_KEYS: Array<[keyof ThemeTokens["m3"], string]> = [
  ["primary", "primary"],
  ["onPrimary", "on-primary"],
  ["primaryContainer", "primary-container"],
  ["onPrimaryContainer", "on-primary-container"],
  ["secondary", "secondary"],
  ["onSecondary", "on-secondary"],
  ["secondaryContainer", "secondary-container"],
  ["onSecondaryContainer", "on-secondary-container"],
  ["tertiary", "tertiary"],
  ["onTertiary", "on-tertiary"],
  ["tertiaryContainer", "tertiary-container"],
  ["onTertiaryContainer", "on-tertiary-container"],
  ["error", "error"],
  ["onError", "on-error"],
  ["errorContainer", "error-container"],
  ["onErrorContainer", "on-error-container"],
  ["surfaceDim", "surface-dim"],
  // m3.surface itself is deliberately not injected as its own CSS var — it's
  // pixel-identical to the existing `background` token (#FAFAF8 both) and
  // m3.surfaceBright is identical to `surfaceRaised` (#FFFFFF both); adding
  // a colliding or confusingly-named third var for the same color would be
  // worse than just documenting the equivalence (see TOKENS.md).
  ["surfaceBright", "surface-bright"],
  ["onSurface", "on-surface"],
  ["onSurfaceVariant", "on-surface-variant"],
  ["outline", "outline"],
  ["outlineVariant", "outline-variant"],
  ["inverseSurface", "inverse-surface"],
  ["inverseOnSurface", "inverse-on-surface"],
  ["inversePrimary", "inverse-primary"],
];

function toCssVars(tokens: ThemeTokens): React.CSSProperties {
  const vars: Record<string, string> = {
    "--ds-color-background": tokens.background,
    "--ds-color-surface": tokens.surface,
    "--ds-color-surface-raised": tokens.surfaceRaised,
    "--ds-color-border": tokens.border,
    "--ds-color-text": tokens.text,
    "--ds-color-text-muted": tokens.textMuted,
    "--ds-color-text-subtle": tokens.textSubtle,
    "--ds-color-on-accent": tokens.onAccent,
    "--ds-font-sans": fontFamily.sans,
    "--ds-font-weight-normal": fontWeight.normal,
    "--ds-font-weight-semibold": fontWeight.semibold,
  };

  for (const step of RAMP_STEPS) {
    vars[`--ds-color-accent-${step}`] = tokens.accent[step];
    vars[`--ds-color-neutral-${step}`] = tokens.neutral[step];
  }

  for (const [key, cssSuffix] of M3_ROLE_KEYS) {
    vars[`--ds-color-${cssSuffix}`] = tokens.m3[key];
  }

  for (const key of SEMANTIC_KEYS) {
    const semantic = tokens[key];
    vars[`--ds-color-${key}-bg`] = semantic.bg;
    vars[`--ds-color-${key}-border`] = semantic.border;
    vars[`--ds-color-${key}-fg`] = semantic.fg;
    vars[`--ds-color-${key}-solid`] = semantic.solid;
    vars[`--ds-color-${key}-on-solid`] = semantic.onSolid;
  }

  for (const size of FONT_SIZE_KEYS) {
    vars[`--ds-font-size-${size}`] = fontSize[size].fontSize;
    vars[`--ds-font-line-height-${size}`] = fontSize[size].lineHeight;
  }

  return vars as React.CSSProperties;
}

export interface ThemeProviderProps {
  theme?: ThemeMode;
  density?: Density;
  className?: string;
  children: React.ReactNode;
}

export function ThemeProvider({
  theme = "light",
  density = "comfortable",
  className = "",
  children,
}: ThemeProviderProps) {
  const tokens = theme === "dark" ? darkTheme : lightTheme;
  const style = useMemo(() => toCssVars(tokens), [tokens]);
  const contextValue = useMemo(() => ({ theme, density }), [theme, density]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <div data-ds-theme={theme} data-ds-density={density} style={style} className={className}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}
